import {useCallback, useState} from 'react'
import {resolverCandidato, type CandidatoIdentidad, type PaqueteDestino, type TarjetaCola} from '@/lib/bateria-cola'
import type {DestinoHoja} from '@/components/bateria/cola/tarjeta-ubicar'

// Vive con las tarjetas y no en `hooks/`: esa es la capa de datos y no puede depender de UI.

/** Destino, buscador y descarte armado se limpian siempre juntos con `limpiar()`. */
export function useColaUbicar(paquetes: PaqueteDestino[]) {
  const [buscando, setBuscando] = useState(false)
  const [destino, setDestino] = useState<DestinoHoja | null>(null)
  // La `ref` y no un booleano: un descarte armado no se dispara sobre otra hoja si el cursor se movió.
  const [descarteArmado, setDescarteArmado] = useState<string | null>(null)

  const limpiar = useCallback(() => {
    setBuscando(false)
    setDestino(null)
    setDescarteArmado(null)
  }, [])

  // Llegan como argumento para que la lista visible y la que responde a los dígitos sean la misma.
  const seleccionarCandidato = useCallback(
    (tarjeta: TarjetaCola, i: number, candidatos: CandidatoIdentidad[]) => {
      const candidato = candidatos[i]
      if (candidato === undefined) return
      const paquete = resolverCandidato(candidato, paquetes)
      setDescarteArmado(null)
      if (!paquete || candidato.numero_pagina_probable === null) {
        setBuscando(true)
        return
      }
      const numero = candidato.numero_pagina_probable
      setDestino({ref: tarjeta.ref, paqueteId: paquete.id, persona: candidato.persona, numero})
    },
    [paquetes]
  )

  // El primer gesto arma y el segundo confirma, igual que `T` y `M`.
  const armarODescartar = useCallback(
    (tarjeta: TarjetaCola, descartar: (tarjeta: TarjetaCola) => void) => {
      if (descarteArmado === tarjeta.ref) descartar(tarjeta)
      else setDescarteArmado(tarjeta.ref)
    },
    [descarteArmado]
  )

  return {buscando, setBuscando, destino, descarteArmado, armarODescartar, limpiar, seleccionarCandidato}
}
